import React, { useMemo } from "react";
import Loader from "react-loaders";
import styled from "@emotion/styled";
import { FormattedMessage, useIntl } from "react-intl";

import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  SortDirection,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import TablePagination from "components/TablePagination";
import SimpleTableHeader from "components/SimpleTableHeader";
import SortableTableHeader from "components/SortableTableHeader";
import ParkkiTheme from "themes/ParkkiTheme";

const Container = styled(Paper)`
  width: 100%;
`;

const EmptyTableRow = styled.div`
  padding: ${ParkkiTheme.spacings.small};
  text-align: center;
  font-style: italic;
`;

type Props = {
  children?: React.ReactNode;
  actions: boolean;
  sortable: {
    orders: { [key: string]: SortDirection | undefined };
    changeSort: Function;
  };
  loading: boolean;
  toggleRowIfAnyResult: boolean;
  headItems: { [key: string]: string };
  pagination: {
    length: number;
    loading: boolean;
    rowsPerPage: number;
    page: number;
    changePage: Function;
    changeRowsPerPage: Function;
  };
};

function TableComponent({
  children,
  actions,
  pagination,
  sortable,
  loading,
  toggleRowIfAnyResult,
  headItems,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();
  const items = React.Children.toArray(children);

  const withEmptyTableRow = useMemo(() => {
    return !(!toggleRowIfAnyResult || loading || items?.length > 0);
  }, [loading, toggleRowIfAnyResult, items]);

  const withTableBody = useMemo(() => {
    return !(
      loading ||
      !Array.isArray(items) ||
      (Array.isArray(items) && items.length === 0)
    );
  }, [loading, items]);

  return (
    <Container>
      <Table>
        {headItems && (
          <TableHead>
            <TableRow>
              {sortable ? (
                <SortableTableHeader
                  headItems={headItems}
                  sortable={sortable}
                />
              ) : (
                <SimpleTableHeader headItems={headItems} />
              )}

              {actions && (
                <TableCell>
                  <FormattedMessage id="buttons.actions" />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
        )}
        {withTableBody ? (
          <TableBody>
            {items.map((item, index) => {
              return <React.Fragment key={index}>{item}</React.Fragment>;
            })}
          </TableBody>
        ) : null}
        <TablePagination pagination={pagination} />
      </Table>

      {loading ? (
        <EmptyTableRow>
          {/* @ts-ignore */}
          <Loader color={ParkkiTheme.colors.blue} type="ball-beat" />
        </EmptyTableRow>
      ) : null}

      {withEmptyTableRow ? (
        <EmptyTableRow>
          {formatMessage({ id: "components.table.anyResult" })}
        </EmptyTableRow>
      ) : null}
    </Container>
  );
}

TableComponent.defaultProps = {
  actionIcon: <DeleteIcon />,
  actionTooltip: null,
  actions: false,
  headItems: null,
  initialSelectedItems: [],
  isCheckboxDisabled: () => false,
  items: [],
  loading: false,
  pagination: false,
  selectable: false,
  sortable: false,
  onAction: null,
  title: "",
  toggleCheckbox: () => true,
  toggleRowIfAnyResult: true,
};

export default TableComponent;
